import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gavel, Info, RefreshCw, Users } from 'lucide-react';
import {
  apiService,
  PlayerBasicInfoDTO,
  PlayerRoleDTO,
  PlayerMoneyDTO,
  NameHistoryEntry,
  HomeListItemDTO,
  HomeCoordsDTO,
  FactionInfoDTO,
  FactionHomeDTO,
  FactionMemberDTO,
  FactionClaimDTO,
  JobsInfoDTO,
  SanctionDTO,
  MarketItemDTO,
  MarketHistoryItemDTO,
  StorePointsDTO,
  StorePointTransactionDTO,
  VaultTransactionDTO,
  OnlinePlayerDTO,
  PlayerSuggestionDTO,
  RandomPlayerDTO,
} from '@/services/api.service';
import { RankManager } from '@/components/RankManager';
import { PlayerAutocomplete } from '@/components/PlayerAutocomplete';
import { SanctionModal } from '@/components/SanctionModal';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import {
  useActiveGameGroup,
  useActiveGroupCapabilities,
  useGameGroupsStore,
  useRanksOnlyGameGroups,
  useUsableGameGroups,
} from '@/stores/gameGroups.store';
import { useModuleEnabled } from '@/stores/projectPages.store';
import { playersSheetMode } from '@/config/panelModules';
import { isSamePlayerUuid } from '@/utils/playerUuid';
import { formatPlayerName, playerLookupName } from '@/utils/playerName';
import { transactionMotif, transactionOrigin } from '@/utils/moneyCategories';
import { joinGroupLabels } from '@/utils/supervision';
import styles from './PlayersPage.module.scss';

/**
 * Métiers d'un jeu à plusieurs groupes (NationsGlory : Nova, Iris) : le mod Jobs écrit dans une base
 * commune, la fiche montre donc l'XP de tous les groupes. Rien pour Paladium ni pour un jeu à un groupe.
 */
function SharedJobsNote() {
  const gameId = useGameStore((s) => s.activeGameId);
  // Entrée sélectionnée telle quelle (référence stable) : zustand 5 relance le rendu à chaque nouveau tableau.
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const labels = (entry?.groups ?? []).map((g) => g.label);
  if (labels.length < 2) return null;
  const list = `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`;
  return <p className={styles.sharedNote}>Métiers communs à {list} : XP et quêtes de tous les groupes.</p>;
}

// State pour chaque section de donnees
interface PlayerData {
  basicInfo: PlayerBasicInfoDTO | null;
  role: PlayerRoleDTO | null;
  money: PlayerMoneyDTO | null;
  storePoints: StorePointsDTO | null;
  nameHistory: NameHistoryEntry[];
  personalHomes: Record<string, HomeListItemDTO[]>;
  faction: FactionInfoDTO | null;
  jobs: JobsInfoDTO | null;
  sanctions: SanctionDTO[];
  marketItems: MarketItemDTO[];
  marketHistoryCount: number;
}

interface LoadingState {
  basicInfo: boolean;
  role: boolean;
  money: boolean;
  storePoints: boolean;
  nameHistory: boolean;
  homes: boolean;
  faction: boolean;
  jobs: boolean;
  sanctions: boolean;
  market: boolean;
}

/** Fiche vide : aucun joueur choisi (vue par défaut, joueurs au hasard). */
const EMPTY_PLAYER_DATA: PlayerData = {
  basicInfo: null,
  role: null,
  money: null,
  storePoints: null,
  nameHistory: [],
  personalHomes: {},
  faction: null,
  jobs: null,
  sanctions: [],
  marketItems: [],
  marketHistoryCount: 0,
};

const ALL_LOADING: LoadingState = {
  basicInfo: true,
  role: true,
  money: true,
  storePoints: true,
  nameHistory: true,
  homes: true,
  faction: true,
  jobs: true,
  sanctions: true,
  market: true,
};

/** Fiche allégée : seules l'identité et, si le satellite sert les grades, le rôle sont lus. */
function lightLoading(readsRole: boolean): LoadingState {
  return {
    basicInfo: true,
    role: readsRole,
    money: false,
    storePoints: false,
    nameHistory: false,
    homes: false,
    faction: false,
    jobs: false,
    sanctions: false,
    market: false,
  };
}

export function PlayersPage() {
  const { uuid: urlUuid } = useParams<{ uuid?: string }>();
  const navigate = useNavigate();

  // Sections liées à un module du projet (Paramètres > Fonctionnalités) : masquées et non chargées
  // quand le module est coupé pour le projet actif.
  const isModuleOn = useModuleEnabled();
  const showFaction = isModuleOn('factions');
  const showJobs = isModuleOn('jobs');
  const modulesRef = useRef({ showFaction, showJobs });
  modulesRef.current = { showFaction, showJobs };

  // Groupes « grades seuls » de l'affectation (satellite avec ranks sans players, comme un lobby
  // sans base) : pas de page Joueurs à eux, la fiche lue ici gère aussi leurs grades, un cadre par
  // groupe. Vide pour un jeu sans groupes et pour des groupes qui servent tous les joueurs.
  const activeGameId = useGameStore((s) => s.activeGameId);
  const ranksOnlyGroups = useRanksOnlyGameGroups(activeGameId);

  // Fiche allégée (cf playersSheetMode) : satellite du groupe actif sans base des joueurs, avec la
  // ProfileAPI (lobby). Recherche par pseudo, identité et grades du groupe, sans aucune requête vers
  // une route lue dans Mongo ; rôle et présence lus seulement si ce satellite les sert (ranks,
  // online). Fiche complète partout ailleurs, Paladium compris (capacités inconnues).
  const capabilities = useActiveGroupCapabilities();
  const light = playersSheetMode(capabilities) === 'light';
  const lightReadsRole = light && !!capabilities?.includes('ranks');
  const lightReadsOnline = light && !!capabilities?.includes('online');

  const [data, setData] = useState<PlayerData>(EMPTY_PLAYER_DATA);

  const [loading, setLoading] = useState<LoadingState>(ALL_LOADING);

  // Modal pour les membres de faction
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [factionMembers, setFactionMembers] = useState<FactionMemberDTO[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Modal pour les claims de faction
  const [showClaimsModal, setShowClaimsModal] = useState(false);
  const [factionClaims, setFactionClaims] = useState<FactionClaimDTO[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(false);

  // Modal pour l'historique du market
  const [showMarketHistoryModal, setShowMarketHistoryModal] = useState(false);
  const [marketHistory, setMarketHistory] = useState<MarketHistoryItemDTO[]>([]);
  const [marketHistoryPage, setMarketHistoryPage] = useState(0);
  const [, setMarketHistoryTotal] = useState(0);
  const [marketHistoryTotalPages, setMarketHistoryTotalPages] = useState(0);
  const [loadingMarketHistory, setLoadingMarketHistory] = useState(false);

  // Modal pour l'historique des store points
  const [showStorePointsHistoryModal, setShowStorePointsHistoryModal] = useState(false);
  const [storePointsHistory, setStorePointsHistory] = useState<StorePointTransactionDTO[]>([]);
  const [storePointsHistoryPage, setStorePointsHistoryPage] = useState(0);
  const [, setStorePointsHistoryTotal] = useState(0);
  const [storePointsHistoryTotalPages, setStorePointsHistoryTotalPages] = useState(0);
  const [loadingStorePointsHistory, setLoadingStorePointsHistory] = useState(false);

  // Modal pour l'historique des transactions monétaires
  const [showMoneyHistoryModal, setShowMoneyHistoryModal] = useState(false);
  const [moneyHistory, setMoneyHistory] = useState<VaultTransactionDTO[]>([]);
  const [moneyHistoryError, setMoneyHistoryError] = useState<string | null>(null);
  const [moneyHistoryPage, setMoneyHistoryPage] = useState(0);
  const [, setMoneyHistoryTotal] = useState(0);
  const [moneyHistoryTotalPages, setMoneyHistoryTotalPages] = useState(0);
  const [loadingMoneyHistory, setLoadingMoneyHistory] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  // Joueur affiché : toujours celui de l'URL (la recherche y mène aussi), aucun sinon.
  const currentUuid = urlUuid ?? null;
  const hasPlayer = currentUuid !== null;

  // Génération de la vue : chaque fiche chargée, et chaque retour à la liste, l'incrémente. Une
  // réponse d'une génération dépassée (fiche quittée, recherche doublée par un clic) est ignorée.
  const viewGen = useRef(0);
  const searchId = useRef(0);

  // Tirage au hasard gardé ici : ouvrir une fiche puis revenir (Précédent) remet la même liste.
  const randomDraw = useRandomPlayersDraw();

  // Sanction directe depuis le profil
  const hasPage = useAuthStore((s) => s.hasPage);
  // Fiche allégée : le satellite du groupe n'a pas de base de modération, la sanction échouerait.
  const canSanction = hasPage('sanctions.create') && !light;
  const [showSanctionModal, setShowSanctionModal] = useState(false);

  // Online status
  const [onlineStatus, setOnlineStatus] = useState<{ online: boolean; player: OnlinePlayerDTO | null } | null>(null);

  // Expanded sections
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());
  const [expandedFactionHomes, setExpandedFactionHomes] = useState(false);

  // Modales de la fiche : elles appartiennent au joueur affiché et se ferment quand il change ou
  // quand on revient à la liste.
  const closeModals = useCallback(() => {
    setShowMembersModal(false);
    setShowClaimsModal(false);
    setShowMarketHistoryModal(false);
    setShowStorePointsHistoryModal(false);
    setShowMoneyHistoryModal(false);
    setShowSanctionModal(false);
  }, []);

  // Charger les donnees d'un joueur (UUID de l'URL). Sans joueur choisi, la page montre des joueurs
  // au hasard et n'interroge plus la fiche du membre du staff connecte (/me).
  const loadPlayerData = useCallback((uuid: string) => {
    const gen = ++viewGen.current;
    // N'applique une réponse que si cette fiche est toujours celle affichée.
    const live = <T,>(apply: (result: T) => void) => (result: T) => {
      if (gen === viewGen.current) apply(result);
    };

    // Fiche vide d'abord : rien du joueur précédent ne reste dans une section dont la requête échoue.
    setData(EMPTY_PLAYER_DATA);
    setLoading(light ? lightLoading(lightReadsRole) : ALL_LOADING);
    setError(null);
    setExpandedServers(new Set());
    setExpandedFactionHomes(false);
    closeModals();
    setOnlineStatus(null);

    // Charger toutes les donnees en parallele
    // Basic Info
    apiService.getBasicInfoByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, basicInfo: result.data! }));
        // Présence par l'uuid de la fiche (fiche allégée : si le satellite sert la présence). Plus par le
        // gamertag : il peut être le pseudo Recube d'un autre joueur, cherché en premier par le satellite.
        if (!light || lightReadsOnline) {
          apiService.checkPlayerOnline(uuid).then(live((onlineResult) => {
            if (onlineResult.data) {
              setOnlineStatus(onlineResult.data);
            }
          }));
        }
      } else if (result.error) {
        setError(result.error);
      }
      setLoading((prev) => ({ ...prev, basicInfo: false }));
    }));

    // Role (fiche allégée : si le satellite sert les grades LuckPerms)
    if (!light || lightReadsRole) {
      apiService.getRoleByUuid(uuid).then(live((result) => {
        if (result.data) {
          setData((prev) => ({ ...prev, role: result.data! }));
        }
        setLoading((prev) => ({ ...prev, role: false }));
      }));
    }

    // Fiche allégée : rien d'autre. Toutes les sections qui suivent sont lues dans les bases du
    // satellite (vault, storepoint, moderation, essentials, factionapi, jobs, market).
    if (light) return;

    // Money
    apiService.getMoneyByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, money: result.data! }));
      }
      setLoading((prev) => ({ ...prev, money: false }));
    }));

    // Store Points
    apiService.getStorePointsByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, storePoints: result.data! }));
      }
      setLoading((prev) => ({ ...prev, storePoints: false }));
    }));

    // Name History
    apiService.getNameHistoryByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, nameHistory: result.data! }));
      }
      setLoading((prev) => ({ ...prev, nameHistory: false }));
    }));

    // Homes
    apiService.getHomesByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, personalHomes: result.data! }));
      }
      setLoading((prev) => ({ ...prev, homes: false }));
    }));

    // Faction (module Factions actif seulement)
    if (modulesRef.current.showFaction) {
      apiService.getFactionByUuid(uuid).then(live((result) => {
        if (result.data) {
          setData((prev) => ({ ...prev, faction: result.data! }));
        }
        setLoading((prev) => ({ ...prev, faction: false }));
      }));
    } else {
      setLoading((prev) => ({ ...prev, faction: false }));
    }

    // Jobs (module Jobs actif seulement)
    if (modulesRef.current.showJobs) {
      apiService.getJobsByUuid(uuid).then(live((result) => {
        if (result.data) {
          setData((prev) => ({ ...prev, jobs: result.data! }));
        }
        setLoading((prev) => ({ ...prev, jobs: false }));
      }));
    } else {
      setLoading((prev) => ({ ...prev, jobs: false }));
    }

    // Sanctions
    apiService.getSanctionsByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, sanctions: result.data! }));
      }
      setLoading((prev) => ({ ...prev, sanctions: false }));
    }));

    // Market Items
    apiService.getMarketItemsByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, marketItems: result.data!.items }));
      }
      setLoading((prev) => ({ ...prev, market: false }));
    }));

    // Market History Count
    apiService.getMarketHistoryCountByUuid(uuid).then(live((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, marketHistoryCount: result.data!.count }));
      }
    }));
  }, [closeModals, light, lightReadsRole, lightReadsOnline]);

  // Joueur de l'URL (lien direct, recherche, clic dans la liste ou sur un membre) ; sans UUID, retour
  // à la liste de joueurs au hasard (ex. bouton Précédent depuis une fiche).
  useEffect(() => {
    if (urlUuid) {
      // UUID dans l'URL - charger ce joueur specifique
      loadPlayerData(urlUuid);
    } else {
      // Les réponses encore en vol de la fiche quittée ne toucheront plus la page.
      viewGen.current++;
      closeModals();
      setError(null);
      setData(EMPTY_PLAYER_DATA);
      setLoading(ALL_LOADING);
      setOnlineStatus(null);
    }
  }, [urlUuid, loadPlayerData, closeModals]);

  // Recherche par le formulaire : mène à /players/<uuid>, comme une suggestion de l'autocomplétion,
  // pour que Précédent et le lien Joueurs ramènent à la liste.
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchInput.trim();
    if (!query) return;

    const id = ++searchId.current;
    const gen = viewGen.current;
    setSearching(true);
    setError(null);

    // D'abord rechercher le joueur pour obtenir son UUID
    const searchResult = await apiService.searchPlayer(query);

    // Une recherche plus récente a pris le relais.
    if (id !== searchId.current) return;
    setSearching(false);
    // Une fiche a été ouverte ou quittée pendant la recherche (clic dans la liste, Précédent) :
    // le dernier geste l'emporte, la réponse de la recherche est ignorée.
    if (gen !== viewGen.current) return;

    if (searchResult.error) {
      setError(searchResult.error);
      return;
    }

    if (searchResult.data) {
      const uuid = searchResult.data.uuid;
      if (uuid === urlUuid) {
        // Joueur déjà affiché : on recharge sa fiche.
        loadPlayerData(uuid);
      } else {
        navigate(`/players/${uuid}`);
      }
    }
  };

  // Liste au hasard et suggestion de l'autocomplétion : la fiche s'ouvre par le pseudo, comme une
  // recherche. Leur uuid vient du roster du satellite, écrit jusqu'à la v1.0.12 avec les octets de
  // chaque moitié inversés (PlayersModule), que la ProfileApi ne connaît pas. L'uuid trouvé par le
  // pseudo n'est pris que s'il désigne le même joueur (identique, ou aux octets inversés) : un
  // gamertag repris par un autre joueur n'ouvre pas sa fiche. Sinon, ou pseudo introuvable : uuid
  // du roster.
  const openPlayerByName = async (player: PlayerSuggestionDTO) => {
    const id = ++searchId.current;
    const gen = viewGen.current;
    setSearching(true);
    setError(null);
    const result = await apiService.searchPlayer(playerLookupName(player.name, player.displayName));
    if (id !== searchId.current) return;
    setSearching(false);
    if (gen !== viewGen.current) return;
    const found = result.data?.uuid;
    navigate(`/players/${found && isSamePlayerUuid(found, player.uuid) ? found : player.uuid}`);
  };

  // Recharge uniquement la section Sanctions (après création depuis le profil)
  const reloadSanctions = () => {
    if (!currentUuid) return;
    const gen = viewGen.current;
    setLoading((prev) => ({ ...prev, sanctions: true }));
    void apiService.getSanctionsByUuid(currentUuid).then((r) => {
      if (gen !== viewGen.current) return;
      if (r.data) setData((prev) => ({ ...prev, sanctions: r.data! }));
      setLoading((prev) => ({ ...prev, sanctions: false }));
    });
  };

  const toggleServer = (server: string) => {
    setExpandedServers((prev) => {
      const next = new Set(prev);
      if (next.has(server)) {
        next.delete(server);
      } else {
        next.add(server);
      }
      return next;
    });
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Inconnu';
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatXp = (xp: number) => {
    if (xp >= 1000000) return `${(xp / 1000000).toFixed(1)}M`;
    if (xp >= 1000) return `${(xp / 1000).toFixed(1)}K`;
    return xp.toString();
  };

  const formatMoney = (money: number) => {
    return money.toLocaleString('fr-FR') + ' $';
  };

  const getServerDisplayName = (server: string) => {
    const match = server.match(/faction-(\d+)/);
    return match ? `Faction ${match[1]}` : server;
  };

  const getSanctionTypeClass = (type: number): string => {
    switch (type) {
      case 0:
        return styles.typeBan ?? '';
      case 1:
        return styles.typeMute ?? '';
      case 2:
        return styles.typeWarn ?? '';
      default:
        return '';
    }
  };

  const handleOpenMembersModal = async () => {
    if (!data.faction) return;

    setShowMembersModal(true);
    setLoadingMembers(true);
    setFactionMembers([]);

    const gen = viewGen.current;
    const result = await apiService.getFactionMembers(data.faction.id);
    // Fiche quittée pendant la requête : la modale a été fermée, rien à remplir.
    if (gen !== viewGen.current) return;
    if (result.data) {
      setFactionMembers(result.data);
    }
    setLoadingMembers(false);
  };

  const handleMemberClick = (memberUuid: string) => {
    setShowMembersModal(false);
    navigate(`/players/${memberUuid}`);
  };

  const handleOpenClaimsModal = async () => {
    if (!data.faction) return;

    setShowClaimsModal(true);
    setLoadingClaims(true);
    setFactionClaims([]);

    const gen = viewGen.current;
    const result = await apiService.getFactionClaims(data.faction.id);
    // Fiche quittée pendant la requête : la modale a été fermée, rien à remplir.
    if (gen !== viewGen.current) return;
    if (result.data) {
      setFactionClaims(result.data);
    }
    setLoadingClaims(false);
  };

  const handleOpenMarketHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowMarketHistoryModal(true);
    setLoadingMarketHistory(true);
    setMarketHistoryPage(page);

    const gen = viewGen.current;
    const result = await apiService.getMarketHistoryByUuid(currentUuid, page, 10);
    // Fiche quittée pendant la requête : la modale a été fermée, rien à remplir.
    if (gen !== viewGen.current) return;
    if (result.data) {
      setMarketHistory(result.data.items);
      setMarketHistoryTotal(result.data.total);
      setMarketHistoryTotalPages(result.data.totalPages);
    }
    setLoadingMarketHistory(false);
  };

  const handleBuyerClick = (buyerUuid: string) => {
    setShowMarketHistoryModal(false);
    navigate(`/players/${buyerUuid}`);
  };

  const handleOpenStorePointsHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowStorePointsHistoryModal(true);
    setLoadingStorePointsHistory(true);
    setStorePointsHistoryPage(page);

    const gen = viewGen.current;
    const result = await apiService.getStorePointTransactionsByUuid(currentUuid, page, 10);
    // Fiche quittée pendant la requête : la modale a été fermée, rien à remplir.
    if (gen !== viewGen.current) return;
    if (result.data) {
      setStorePointsHistory(result.data.items);
      setStorePointsHistoryTotal(result.data.total);
      setStorePointsHistoryTotalPages(result.data.totalPages);
    }
    setLoadingStorePointsHistory(false);
  };

  const handleOpenMoneyHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowMoneyHistoryModal(true);
    setLoadingMoneyHistory(true);
    setMoneyHistoryPage(page);

    const gen = viewGen.current;
    const result = await apiService.getMoneyTransactionsByUuid(currentUuid, page, 10);
    // Fiche quittée pendant la requête : la modale a été fermée, rien à remplir.
    if (gen !== viewGen.current) return;
    if (result.data) {
      setMoneyHistory(result.data.items);
      setMoneyHistoryTotal(result.data.total);
      setMoneyHistoryTotalPages(result.data.totalPages);
      setMoneyHistoryError(null);
    } else {
      // Une base qui ne répond pas n'est pas un historique vide : le dire.
      setMoneyHistory([]);
      setMoneyHistoryError(result.error ?? "L'historique n'a pas pu être lu.");
    }
    setLoadingMoneyHistory(false);
  };

  const formatStorePoints = (points: number) => {
    return points.toLocaleString('fr-FR') + ' PB';
  };

  const formatItemName = (name: string) => {
    if (!name) return 'Item inconnu';
    // Formater le nom: remplacer les underscores par des espaces et capitaliser
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatItemIdentifier = (identifier: string) => {
    if (!identifier) return '';
    // Retirer le namespace (minecraft:, paladium:, etc.)
    const parts = identifier.split(':');
    return parts.length > 1 ? parts[1] : identifier;
  };

  // Calculer si le joueur a une sanction active (ban ou mute)
  const getActiveSanction = () => {
    if (!data.sanctions || data.sanctions.length === 0) return null;

    const now = Date.now();
    for (const sanction of data.sanctions) {
      // Type 0 = ban, Type 1 = mute (ignorer les warns type 2)
      if (sanction.type > 1) continue;
      if (sanction.revoked) continue;

      // Si temporaire et expiree, ignorer
      if (sanction.temporary && sanction.expiryAt && sanction.expiryAt < now) continue;

      // Sanction active trouvee
      return sanction;
    }
    return null;
  };

  const activeSanction = !loading.sanctions ? getActiveSanction() : null;

  // Fiche allégée : l'identité seule décide de l'attente (le rôle peut ne pas être lu).
  const isFullyLoading = loading.basicInfo && (light || loading.role);

  if (hasPlayer && isFullyLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Search bar */}
      <header className={styles.header}>
        <h1 className={styles.title}>Joueurs</h1>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <PlayerAutocomplete
            value={searchInput}
            onChange={setSearchInput}
            onSelect={openPlayerByName}
            suggest={!light}
            placeholder="Rechercher un joueur (pseudo Recube ou gamertag)..."
            inputClassName={styles.searchInput}
            className={styles.searchBox}
          />
          <button type="submit" className={styles.searchButton} disabled={searching}>
            {searching ? <div className={styles.spinnerSmall} /> : 'Rechercher'}
          </button>
        </form>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {!hasPlayer && (light
        ? <LightSheetSearch />
        : <RandomPlayers draw={randomDraw} onOpen={openPlayerByName} />)}

      {hasPlayer && data.basicInfo && (
        <div className={styles.profileContainer}>
          {/* Active Sanction Banner */}
          {activeSanction && (
            <div className={`${styles.sanctionBanner} ${activeSanction.type === 0 ? styles.banBanner : styles.muteBanner}`}>
              <span className={styles.sanctionIcon}>{activeSanction.type === 0 ? '🚫' : '🔇'}</span>
              <div className={styles.sanctionBannerContent}>
                <span className={styles.sanctionBannerTitle}>
                  {activeSanction.type === 0 ? 'JOUEUR BANNI' : 'JOUEUR MUTE'}
                </span>
                <span className={styles.sanctionBannerReason}>
                  {activeSanction.description || 'Aucune raison specifiee'}
                </span>
                {activeSanction.temporary && activeSanction.expiryAt && (
                  <span className={styles.sanctionBannerExpiry}>
                    Expire le {formatDate(activeSanction.expiryAt)}
                  </span>
                )}
                {!activeSanction.temporary && (
                  <span className={styles.sanctionBannerExpiry}>Permanent</span>
                )}
              </div>
              <span className={styles.sanctionBannerStaff}>
                Par {formatPlayerName(activeSanction.staffName, activeSanction.staffDisplayName, 'Systeme')}
              </span>
            </div>
          )}

          {/* Player Header */}
          <section className={styles.playerHeader}>
            <div className={styles.skinContainer}>
              <img
                src="https://mc-heads.net/avatar/MHF_Steve/128"
                alt="Player skin"
                className={styles.skinImage}
              />
            </div>
            <div className={styles.playerInfo}>
              <div className={styles.playerNameRow}>
                <h2 className={styles.playerName}>{formatPlayerName(data.basicInfo.name, data.basicInfo.displayName)}</h2>
                {onlineStatus && (
                  onlineStatus.online ? (
                    <span className={styles.onlineBadge}>
                      <span className={styles.onlineDot} />
                      En ligne - {onlineStatus.player?.serverName || 'Serveur inconnu'}
                    </span>
                  ) : (
                    <span className={styles.offlineBadge}>
                      <span className={styles.offlineDot} />
                      Hors ligne
                    </span>
                  )
                )}
                {canSanction && currentUuid && !loading.basicInfo && (
                  <button
                    type="button"
                    className={styles.sanctionBtn}
                    onClick={() => setShowSanctionModal(true)}
                  >
                    <Gavel size={15} strokeWidth={2} />
                    Sanctionner
                  </button>
                )}
              </div>
              <div className={styles.playerMeta}>
                {loading.role ? (
                  <span className={styles.playerRole}>...</span>
                ) : (
                  <span className={styles.playerRole}>
                    {data.role?.roleDisplayName || data.role?.role || 'Joueur'}
                  </span>
                )}
                <span className={styles.playerUuid} title={data.basicInfo.uuid}>
                  {data.basicInfo.uuid}
                </span>
              </div>
              <div className={styles.playerDates}>
                <div>
                  <span className={styles.dateLabel}>Premiere connexion:</span>
                  <span className={styles.dateValue}>{formatDate(data.basicInfo.firstConnected)}</span>
                </div>
                <div>
                  <span className={styles.dateLabel}>Derniere connexion:</span>
                  <span className={styles.dateValue}>{formatDate(data.basicInfo.lastConnected)}</span>
                </div>
              </div>
            </div>
            {/* Solde et points boutique : bases vault et storepoint, absents de la fiche allégée */}
            {!light && (<>
            {/* Money Card */}
            <div
              className={styles.moneyCard}
              onClick={() => handleOpenMoneyHistoryModal(0)}
              title="Cliquer pour voir l'historique"
            >
              <span className={styles.moneyIcon}>$</span>
              <div className={styles.moneyInfo}>
                <span className={styles.moneyLabel}>Solde</span>
                {loading.money ? (
                  <span className={styles.moneyValue}>...</span>
                ) : (
                  <span className={styles.moneyValue}>{formatMoney(data.money?.money || 0)}</span>
                )}
              </div>
            </div>
            {/* Points Boutique Card */}
            <div
              className={styles.storePointsCard}
              onClick={() => handleOpenStorePointsHistoryModal(0)}
              title="Cliquer pour voir l'historique"
            >
              <span className={styles.storePointsIcon}>💎</span>
              <div className={styles.storePointsInfo}>
                <span className={styles.storePointsLabel}>Points Boutique</span>
                {loading.storePoints ? (
                  <span className={styles.storePointsValue}>...</span>
                ) : (
                  <span className={styles.storePointsValue}>{formatStorePoints(data.storePoints?.totalStorePoints || 0)}</span>
                )}
              </div>
            </div>
            </>)}
          </section>

          <div className={styles.grid}>
            {/* Rank Manager */}
            {currentUuid && data.basicInfo && (
              <RankManager
                playerUuid={currentUuid}
                playerName={formatPlayerName(data.basicInfo.name, data.basicInfo.displayName)}
                onRankChange={() => {
                  // Reload role when rank changes
                  if (currentUuid) {
                    const gen = viewGen.current;
                    apiService.getRoleByUuid(currentUuid).then((r) => {
                      if (gen === viewGen.current && r.data) setData((prev) => ({ ...prev, role: r.data! }));
                    });
                  }
                }}
              />
            )}

            {/* Grades du joueur dans chaque groupe « grades seuls » : mêmes droits (grades gérables
                lus dans ce groupe), toutes les requêtes à groupe imposé. Le rôle affiché reste
                celui du groupe actif, rien à recharger. */}
            {currentUuid && data.basicInfo && ranksOnlyGroups.map((g) => (
              <RankManager
                key={`ranks-${g.key}`}
                playerUuid={currentUuid}
                playerName={formatPlayerName(data.basicInfo?.name, data.basicInfo?.displayName)}
                group={{ key: g.key, label: g.label, color: g.color }}
                unavailable={g.available ? undefined : (g.reason ?? `${g.label} indisponible.`)}
              />
            ))}

            {/* Fiche allégée : ce qu'elle ne montre pas, et où trouver la fiche complète. */}
            {light && <LightSheetNote />}

            {/* Sections lues dans les bases des joueurs : fiche complète seulement. */}
            {!light && (<>
            {/* Name History */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Historique des pseudos</h3>
              {loading.nameHistory ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.nameHistory.length === 0 ? (
                <p className={styles.emptyText}>Aucun historique</p>
              ) : (
                <ul className={styles.nameHistoryList}>
                  {data.nameHistory.map((entry, idx) => (
                    <li key={idx} className={styles.nameHistoryItem}>
                      <span className={styles.historyName}>{entry.name}</span>
                      <span className={styles.historyDate}>{formatDate(entry.firstSeen)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Personal Homes */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Homes personnels</h3>
              {loading.homes ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : Object.keys(data.personalHomes).length === 0 ? (
                <p className={styles.emptyText}>Aucun home</p>
              ) : (
                <div className={styles.homesList}>
                  {Object.entries(data.personalHomes).map(([server, homes]) => (
                    <div key={server} className={styles.serverGroup}>
                      <button className={styles.serverHeader} onClick={() => toggleServer(server)}>
                        <span className={styles.serverName}>
                          {getServerDisplayName(server)}
                          <span className={styles.homeCount}>{homes.length}</span>
                        </span>
                        <span className={styles.expandIcon}>{expandedServers.has(server) ? '−' : '+'}</span>
                      </button>
                      {expandedServers.has(server) && (
                        <ul className={styles.homeItems}>
                          {homes.map((home, idx) => (
                            <HomeItem
                              key={idx}
                              home={home}
                              server={server}
                              playerUuid={currentUuid}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Faction Info : seulement si le module Factions est actif pour le projet */}
            {showFaction && (<>
            <section className={styles.card + ' ' + styles.factionCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>⚔</span>
                Faction
              </h3>
              {loading.faction ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : !data.faction ? (
                <p className={styles.emptyText}>Aucune faction</p>
              ) : (
                <div className={styles.factionInfo}>
                  <div className={styles.factionHeader}>
                    <div className={styles.factionTitleBlock}>
                      <span className={styles.factionName}>{data.faction.name}</span>
                      {data.faction.playerRoles && data.faction.playerRoles.length > 0 && (
                        <span className={styles.factionRole}>{data.faction.playerRoles.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  {data.faction.description && (
                    <div className={styles.factionDescriptionBlock}>
                      <span className={styles.infoLabel}>Description</span>
                      <p className={styles.factionDescription}>{data.faction.description}</p>
                    </div>
                  )}
                  <div className={styles.factionStats}>
                    <div className={`${styles.statItem} ${styles.statMembers}`} onClick={handleOpenMembersModal}>
                      <span className={styles.statValue}>{data.faction.membersCount}</span>
                      <span className={styles.statLabel}>Membres</span>
                      <span className={styles.statClickHint}>Cliquer pour voir</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statClaims}`} onClick={handleOpenClaimsModal}>
                      <span className={styles.statValue}>{data.faction.claimsCount}</span>
                      <span className={styles.statLabel}>Claims</span>
                      <span className={styles.statClickHint}>Cliquer pour voir</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statBank}`}>
                      <span className={styles.statValue}>{formatMoney(data.faction.bankBalance || 0)}</span>
                      <span className={styles.statLabel}>Banque</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statElo}`}>
                      <span className={styles.statValue}>{data.faction.elo || 0}</span>
                      <span className={styles.statLabel}>ELO</span>
                    </div>
                  </div>

                  {/* Faction Homes */}
                  {data.faction.homes && data.faction.homes.length > 0 && (
                    <div className={styles.factionHomes}>
                      <button
                        className={styles.serverHeader}
                        onClick={() => setExpandedFactionHomes(!expandedFactionHomes)}
                      >
                        <span className={styles.serverName}>
                          Homes de faction
                          <span className={styles.homeCount}>{data.faction.homes.length}</span>
                        </span>
                        <span className={styles.expandIcon}>{expandedFactionHomes ? '−' : '+'}</span>
                      </button>
                      {expandedFactionHomes && (
                        <ul className={styles.homeItems}>
                          {data.faction.homes.map((home, idx) => (
                            <FactionHomeItem key={idx} home={home} />
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Members Modal */}
            {showMembersModal && data.faction && (
              <div className={styles.modalOverlay} onClick={() => setShowMembersModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3>Membres de {data.faction.name}</h3>
                    <button className={styles.modalClose} onClick={() => setShowMembersModal(false)}>×</button>
                  </div>
                  <div className={styles.modalContent}>
                    {loadingMembers ? (
                      <div className={styles.modalLoading}>
                        <div className={styles.spinnerSmall} />
                        <span>Chargement des membres...</span>
                      </div>
                    ) : factionMembers.length === 0 ? (
                      <p className={styles.modalNote}>Aucun membre trouve ou vous n'avez pas la permission.</p>
                    ) : (
                      <ul className={styles.membersList}>
                        {factionMembers.map((member) => (
                          <FactionMemberItem
                            key={member.uuid}
                            member={member}
                            onClick={() => handleMemberClick(member.uuid)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Claims Modal */}
            {showClaimsModal && data.faction && (
              <div className={styles.modalOverlay} onClick={() => setShowClaimsModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3>Claims de {data.faction.name}</h3>
                    <button className={styles.modalClose} onClick={() => setShowClaimsModal(false)}>×</button>
                  </div>
                  <div className={styles.modalContent}>
                    {loadingClaims ? (
                      <div className={styles.modalLoading}>
                        <div className={styles.spinnerSmall} />
                        <span>Chargement des claims...</span>
                      </div>
                    ) : factionClaims.length === 0 ? (
                      <p className={styles.modalNote}>Aucun claim trouve ou vous n'avez pas la permission.</p>
                    ) : (
                      <div className={styles.claimsList}>
                        {/* Group claims by server */}
                        {Object.entries(
                          factionClaims.reduce((acc, claim) => {
                            if (!acc[claim.server]) acc[claim.server] = 0;
                            (acc[claim.server] as number) += claim.count;
                            return acc;
                          }, {} as Record<string, number>)
                        ).map(([server, totalClaims]) => (
                          <div key={server} className={styles.claimsServerGroup}>
                            <div className={styles.claimsServerHeader}>
                              <span className={styles.claimsServerName}>{getServerDisplayName(server)}</span>
                              <span className={styles.claimsServerTotal}>
                                {totalClaims} claims
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </>)}

            {/* Jobs : seulement si le module Jobs est actif pour le projet */}
            {showJobs && (
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Jobs</h3>
              <SharedJobsNote />
              {loading.jobs ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : !data.jobs || !data.jobs.jobs || data.jobs.jobs.length === 0 ? (
                <p className={styles.emptyText}>Aucun job</p>
              ) : (
                <div className={styles.jobsList}>
                  {data.jobs.jobs.map((job) => (
                    <div key={job.id} className={styles.jobItem}>
                      <span className={styles.jobName}>{job.name}</span>
                      <span className={styles.jobXp}>{formatXp(job.xp)} XP</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Daily Quests */}
              {data.jobs && data.jobs.dailyQuests && data.jobs.dailyQuests.length > 0 && (
                <div className={styles.dailyQuests}>
                  <h4 className={styles.subTitle}>Quetes journalieres</h4>
                  {data.jobs.dailyQuests.map((quest, idx) => (
                    <div key={idx} className={`${styles.questItem} ${quest.completed ? styles.completed : ''}`}>
                      <div className={styles.questHeader}>
                        <span className={styles.questName}>{quest.name}</span>
                        <span className={styles.questReward}>+{quest.xpReward} XP</span>
                      </div>
                      <p className={styles.questDescription}>{quest.description}</p>
                      <div className={styles.questProgress}>
                        <div
                          className={styles.questProgressBar}
                          style={{ width: `${Math.min(100, (quest.currentProgress / quest.targetValue) * 100)}%` }}
                        />
                        <span className={styles.questProgressText}>
                          {quest.currentProgress} / {quest.targetValue}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            )}

            {/* Market */}
            <section className={styles.card + ' ' + styles.marketCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>🏪</span>
                Market
                {data.marketHistoryCount > 0 && (
                  <button
                    className={styles.marketHistoryButton}
                    onClick={() => handleOpenMarketHistoryModal(0)}
                    title="Voir l'historique des ventes"
                  >
                    {data.marketHistoryCount} ventes
                  </button>
                )}
              </h3>
              {loading.market ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.marketItems.length === 0 ? (
                <p className={styles.emptyText}>Aucun item en vente</p>
              ) : (
                <div className={styles.marketList}>
                  <table className={styles.marketTable}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Quantite</th>
                        <th>Prix</th>
                        <th>Expiration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.marketItems.map((item) => (
                        <tr key={item.id}>
                          <td className={styles.marketItemName}>
                            <span className={styles.itemName}>{formatItemName(item.name)}</span>
                            <span className={styles.itemId}>{formatItemIdentifier(item.identifier)}</span>
                          </td>
                          <td className={styles.marketItemCount}>{item.count}</td>
                          <td className={styles.marketItemPrice}>
                            {item.price > 0 && <span>{item.price.toLocaleString()} $</span>}
                            {item.pbPrice > 0 && <span className={styles.pbPrice}>{item.pbPrice} PB</span>}
                          </td>
                          <td className={styles.marketItemExpiry}>
                            {item.expirationDate ? formatDate(item.expirationDate) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Sanctions */}
            <section className={styles.card + ' ' + styles.fullWidth}>
              <h3 className={styles.cardTitle}>Sanctions</h3>
              {loading.sanctions ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.sanctions.length === 0 ? (
                <p className={styles.emptyText}>Aucune sanction</p>
              ) : (
                <div className={styles.sanctionsList}>
                  <table className={styles.sanctionsTable}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Staff</th>
                        <th>Raison</th>
                        <th>Duree</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sanctions.map((sanction) => (
                        <SanctionRow
                          key={sanction.id}
                          sanction={sanction}
                          getTypeClass={getSanctionTypeClass}
                          formatDate={formatDate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            </>)}
          </div>
        </div>
      )}

      {/* Modal Historique Market */}
      {showMarketHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowMarketHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des ventes</h3>
              <button className={styles.modalClose} onClick={() => setShowMarketHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingMarketHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : marketHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune vente dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.marketHistoryTable}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qte</th>
                        <th>Prix</th>
                        <th>Acheteur</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketHistory.map((item) => (
                        <tr key={item.id}>
                          <td className={styles.marketItemName}>
                            <span className={styles.itemName}>{formatItemName(item.name)}</span>
                          </td>
                          <td>{item.count}</td>
                          <td className={styles.marketItemPrice}>
                            {item.price > 0 && <span>{item.price.toLocaleString()} $</span>}
                            {item.pbPrice > 0 && <span className={styles.pbPrice}>{item.pbPrice} PB</span>}
                          </td>
                          <td>
                            {item.buyerUuid ? (
                              <button
                                className={styles.buyerLink}
                                onClick={() => handleBuyerClick(item.buyerUuid!)}
                              >
                                {formatPlayerName(item.buyerName, item.buyerDisplayName)}
                              </button>
                            ) : (
                              formatPlayerName(item.buyerName, item.buyerDisplayName)
                            )}
                          </td>
                          <td>{item.createdDate ? formatDate(item.createdDate) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {marketHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={marketHistoryPage === 0}
                        onClick={() => handleOpenMarketHistoryModal(marketHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {marketHistoryPage + 1} / {marketHistoryTotalPages}
                      </span>
                      <button
                        disabled={marketHistoryPage >= marketHistoryTotalPages - 1}
                        onClick={() => handleOpenMarketHistoryModal(marketHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Points Boutique */}
      {showStorePointsHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowStorePointsHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des transactions Points Boutique</h3>
              <button className={styles.modalClose} onClick={() => setShowStorePointsHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingStorePointsHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : storePointsHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune transaction dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.storePointsHistoryTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Raison</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storePointsHistory.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.timestamp)}</td>
                          <td className={transaction.amount >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                            {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toLocaleString()} PB
                          </td>
                          <td className={styles.transactionReason}>{transaction.reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {storePointsHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={storePointsHistoryPage === 0}
                        onClick={() => handleOpenStorePointsHistoryModal(storePointsHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {storePointsHistoryPage + 1} / {storePointsHistoryTotalPages}
                      </span>
                      <button
                        disabled={storePointsHistoryPage >= storePointsHistoryTotalPages - 1}
                        onClick={() => handleOpenStorePointsHistoryModal(storePointsHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Transactions Monétaires */}
      {showMoneyHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowMoneyHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des transactions</h3>
              <button className={styles.modalClose} onClick={() => setShowMoneyHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingMoneyHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : moneyHistoryError ? (
                <p className={styles.modalNote}>Historique illisible pour le moment : {moneyHistoryError}</p>
              ) : moneyHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune transaction dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.moneyHistoryTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Origine</th>
                        <th>Motif</th>
                        <th>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyHistory.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.createdDate)}</td>
                          <td>{transactionOrigin(transaction)}</td>
                          <td>{transactionMotif(transaction)}</td>
                          <td className={transaction.amount >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                            {transaction.amount >= 0 ? '+' : ''}{formatMoney(transaction.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {moneyHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={moneyHistoryPage === 0}
                        onClick={() => handleOpenMoneyHistoryModal(moneyHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {moneyHistoryPage + 1} / {moneyHistoryTotalPages}
                      </span>
                      <button
                        disabled={moneyHistoryPage >= moneyHistoryTotalPages - 1}
                        onClick={() => handleOpenMoneyHistoryModal(moneyHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showSanctionModal && data.basicInfo && (
        <SanctionModal
          initialPlayer={currentUuid || data.basicInfo.name || ''}
          playerLabel={formatPlayerName(data.basicInfo.name, data.basicInfo.displayName, currentUuid || '')}
          lockPlayer
          onClose={() => setShowSanctionModal(false)}
          onCreated={() => { setShowSanctionModal(false); reloadSanctions(); }}
        />
      )}
    </div>
  );
}

/**
 * Groupe actif de la fiche allégée, et groupes de l'affectation qui servent la fiche complète
 * (capacité players), pour dire où la trouver.
 */
function useLightSheetGroups(): { label: string; fullSheetGroups: string[] } {
  const gameId = useGameStore((s) => s.activeGameId);
  const group = useActiveGameGroup();
  const usable = useUsableGameGroups(gameId);
  const fullSheetGroups = usable
    .filter((g) => g.key !== group?.key && (g.capabilities ?? []).includes('players'))
    .map((g) => g.label);
  return { label: group?.label ?? 'ce groupe', fullSheetGroups };
}

/** Où trouver la fiche complète (« Fiche complète sous Nova et Iris... »), rien sans autre groupe. */
function fullSheetHint(groups: string[]): string {
  return groups.length > 0
    ? ` Fiche complète sous ${joinGroupLabels(groups)} : change de groupe dans le sélecteur.`
    : '';
}

/**
 * Vue par défaut de la fiche allégée : pas de joueurs au hasard (lus dans la base stats, absente
 * de ce satellite), seulement la recherche par pseudo.
 */
function LightSheetSearch() {
  const { label, fullSheetGroups } = useLightSheetGroups();
  return (
    <section className={styles.randomCard}>
      <div className={styles.randomEmpty}>
        <Users size={40} strokeWidth={1.5} />
        <p>Recherche un joueur par son pseudo pour ouvrir sa fiche.</p>
        <p className={styles.lightHint}>
          Fiche allégée sous {label} : identité et grades du groupe.{fullSheetHint(fullSheetGroups)}
        </p>
      </div>
    </section>
  );
}

/** Note de la fiche allégée, sous les grades : ce qu'elle ne montre pas, et pourquoi. */
function LightSheetNote() {
  const { label, fullSheetGroups } = useLightSheetGroups();
  return (
    <section className={`${styles.card} ${styles.fullWidth} ${styles.lightNote}`}>
      <Info size={16} strokeWidth={2} className={styles.lightNoteIcon} aria-hidden />
      <p>
        Fiche allégée sous {label} : son satellite n'a pas de base de joueurs. Solde, points boutique,
        historique des pseudos, homes, faction, métiers, market et sanctions n'y sont pas lus.
        {fullSheetHint(fullSheetGroups)}
      </p>
    </section>
  );
}

/** Joueurs tirés au hasard quand aucun joueur n'est choisi (le satellite en rend 50 au plus). */
const RANDOM_PLAYERS_COUNT = 24;

/** Dernière présence d'un joueur de la liste, relative tant qu'elle est récente. */
function lastSeenLabel(ts: number | null): string {
  if (!ts) return 'Dernière présence inconnue';
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return "Vu à l'instant";
  if (min < 60) return `Vu il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Vu il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 60) return `Vu il y a ${d} j`;
  return `Vu le ${new Date(ts).toLocaleDateString('fr-FR')}`;
}

/** Tirage au hasard : `idle` tant que la liste n'a jamais été affichée. */
interface RandomPlayersDraw {
  players: RandomPlayerDTO[];
  total: number | null;
  loading: boolean;
  status: 'idle' | 'ok' | 'unsupported' | 'error';
  draw: () => Promise<void>;
}

/**
 * État du tirage, porté par PlayersPage : l'instance de la page reste la même entre /players et
 * /players/:uuid, la liste survit donc à l'ouverture d'une fiche. Elle repart de zéro au changement
 * de jeu ou de groupe, la page étant alors remontée (clé de l'Outlet).
 */
function useRandomPlayersDraw(): RandomPlayersDraw {
  const [players, setPlayers] = useState<RandomPlayerDTO[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<RandomPlayersDraw['status']>('idle');
  // Seul le dernier tirage demandé s'affiche (clics rapides sur « Autre tirage »).
  const drawId = useRef(0);

  const draw = useCallback(async () => {
    const id = ++drawId.current;
    setLoading(true);
    const res = await apiService.getRandomPlayers(RANDOM_PLAYERS_COUNT);
    if (id !== drawId.current) return;
    if (res.data) {
      setPlayers(res.data.players ?? []);
      setTotal(typeof res.data.total === 'number' ? res.data.total : null);
      setStatus('ok');
    } else {
      setPlayers([]);
      setTotal(null);
      setStatus(res.status === 404 ? 'unsupported' : 'error');
    }
    setLoading(false);
  }, []);

  return { players, total, loading, status, draw };
}

/**
 * Vue par défaut de la page Joueurs : des joueurs connus du satellite du groupe actif, tirés au
 * hasard, chacun ouvrant sa fiche comme un résultat de recherche. Satellite d'avant cette route
 * (404) : état neutre, la recherche suffit. Jamais le bandeau d'erreur de la page.
 * Tire seulement au premier affichage et sur « Autre tirage » : un retour depuis une fiche remet la
 * liste déjà vue.
 */
function RandomPlayers({ draw: state, onOpen }: { draw: RandomPlayersDraw; onOpen: (player: RandomPlayerDTO) => void }) {
  const { players, total, loading, status, draw } = state;

  useEffect(() => {
    if (status === 'idle' && !loading) void draw();
  }, [status, loading, draw]);

  if (!loading && status === 'unsupported') {
    return (
      <section className={styles.randomCard}>
        <div className={styles.randomEmpty}>
          <Users size={40} strokeWidth={1.5} />
          <p>Recherche un joueur par son pseudo pour ouvrir sa fiche.</p>
        </div>
      </section>
    );
  }

  const firstLoad = status === 'idle' || (loading && players.length === 0);

  return (
    <section className={styles.randomCard}>
      <div className={styles.randomHeader}>
        <h3 className={styles.randomTitle}>
          <Users size={18} strokeWidth={2} />
          Joueurs au hasard
          {status === 'ok' && total !== null && total > 0 && (
            <span className={styles.randomTotal}>parmi {total.toLocaleString('fr-FR')} joueurs connus</span>
          )}
        </h3>
        <button type="button" className={styles.randomRefresh} onClick={() => void draw()} disabled={loading}>
          <RefreshCw size={14} strokeWidth={2} className={loading ? styles.spinning : undefined} />
          {status === 'error' ? 'Réessayer' : 'Autre tirage'}
        </button>
      </div>

      {firstLoad ? (
        <div className={styles.randomLoading}><div className={styles.spinner} /></div>
      ) : status === 'error' ? (
        <p className={styles.emptyText}>Liste momentanément indisponible. Recherche un joueur par son pseudo ou réessaie.</p>
      ) : players.length === 0 ? (
        <p className={styles.emptyText}>Aucun joueur connu pour l'instant. Recherche un joueur par son pseudo.</p>
      ) : (
        <ul className={`${styles.randomGrid} ${loading ? styles.randomGridLoading : ''}`}>
          {players.map((p) => (
            <li key={p.uuid}>
              <button type="button" className={styles.randomPlayer} onClick={() => onOpen(p)} title={p.uuid}>
                <img
                  src={`https://mc-heads.net/avatar/${encodeURIComponent(p.name)}/40`}
                  alt=""
                  loading="lazy"
                  className={styles.randomAvatar}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    if (!img.src.includes('MHF_Steve')) img.src = 'https://mc-heads.net/avatar/MHF_Steve/40';
                  }}
                />
                <span className={styles.randomInfo}>
                  <span className={styles.randomName}>{formatPlayerName(p.name, p.displayName)}</span>
                  {p.online ? (
                    <span className={styles.randomOnline}>
                      <span className={styles.memberOnlineDot} />
                      En ligne
                    </span>
                  ) : (
                    <span className={styles.randomSeen}>{lastSeenLabel(p.lastSeen)}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HomeItem({
  home,
  server,
  playerUuid,
}: {
  home: HomeListItemDTO;
  server: string;
  playerUuid: string | null;
}) {
  const [coords, setCoords] = useState<HomeCoordsDTO | null>(null);
  const [loadingCoords, setLoadingCoords] = useState(false);
  const [coordsError, setCoordsError] = useState<string | null>(null);

  const handleClick = async () => {
    // Si on a deja les coords, toggle l'affichage
    if (coords) {
      setCoords(null);
      return;
    }

    // Sinon, charger les coords
    setLoadingCoords(true);
    setCoordsError(null);

    const result = playerUuid
      ? await apiService.getHomeCoordsbyUuid(playerUuid, server, home.name)
      : await apiService.getMyHomeCoords(server, home.name);

    if (result.error) {
      setCoordsError(result.error);
    } else if (result.data) {
      setCoords(result.data);
    }
    setLoadingCoords(false);
  };

  return (
    <li className={styles.homeItem} onClick={handleClick}>
      <span className={styles.homeName}>{home.name}</span>
      {loadingCoords && <span className={styles.homeCoords}>...</span>}
      {coordsError && <span className={styles.homeCoords} style={{ color: '#C53030' }}>Pas de permission</span>}
      {coords && (
        <span className={styles.homeCoords}>
          {Math.round(coords.x)}, {Math.round(coords.y)}, {Math.round(coords.z)} ({coords.world})
        </span>
      )}
    </li>
  );
}

function FactionHomeItem({ home }: { home: FactionHomeDTO }) {
  const [showCoords, setShowCoords] = useState(false);
  const serverName = home.server.replace('faction-', 'F');

  return (
    <li className={styles.homeItem} onClick={() => setShowCoords(!showCoords)}>
      <span className={styles.homeName}>[{serverName}] Home</span>
      {showCoords && (
        <span className={styles.homeCoords}>
          {home.x}, {home.y}, {home.z} ({home.world})
        </span>
      )}
    </li>
  );
}

function FactionMemberItem({
  member,
  onClick,
}: {
  member: FactionMemberDTO;
  onClick: () => void;
}) {
  const [onlineStatus, setOnlineStatus] = useState<{ online: boolean; player: OnlinePlayerDTO | null } | null>(null);

  useEffect(() => {
    // Présence par l'uuid du membre : son gamertag peut être le pseudo Recube d'un autre joueur.
    if (member.uuid) {
      apiService.checkPlayerOnline(member.uuid).then((result) => {
        if (result.data) {
          setOnlineStatus(result.data);
        }
      });
    }
  }, [member.uuid]);

  return (
    <li className={styles.memberItem} onClick={onClick}>
      <div className={styles.memberLeft}>
        <img
          src="https://mc-heads.net/avatar/MHF_Steve/32"
          alt=""
          className={styles.memberAvatar}
        />
        <div className={styles.memberInfo}>
          <span className={styles.memberName}>{formatPlayerName(member.name, member.displayName, member.uuid)}</span>
          {member.roles && member.roles.length > 0 && (
            <span className={styles.memberRoles}>{member.roles.join(', ')}</span>
          )}
        </div>
      </div>
      <div className={styles.memberRight}>
        {onlineStatus && (
          onlineStatus.online ? (
            <span className={styles.memberOnlineBadge}>
              <span className={styles.memberOnlineDot} />
              {onlineStatus.player?.serverName || 'En ligne'}
            </span>
          ) : (
            <span className={styles.memberOfflineBadge}>
              <span className={styles.memberOfflineDot} />
              Hors ligne
            </span>
          )
        )}
        <span className={styles.memberArrow}>→</span>
      </div>
    </li>
  );
}

function SanctionRow({
  sanction,
  getTypeClass,
  formatDate,
}: {
  sanction: SanctionDTO;
  getTypeClass: (type: number) => string;
  formatDate: (ts: number | null) => string;
}) {
  return (
    <tr className={sanction.revoked ? styles.revoked : ''}>
      <td>
        <span className={`${styles.typeBadge} ${getTypeClass(sanction.type)}`}>{sanction.typeName}</span>
      </td>
      <td>{formatPlayerName(sanction.staffName, sanction.staffDisplayName, 'Systeme')}</td>
      <td className={styles.reasonCell}>{sanction.description || '-'}</td>
      <td>{sanction.duration || '-'}</td>
      <td>{formatDate(sanction.createAt)}</td>
    </tr>
  );
}
